import React, { useState, useEffect, useMemo } from 'react';
import { authFetch } from './auth';
import { useI18n } from './i18n';
import { getCurrentUserId } from './attachments';
import { sheetToWineParams, isFilled } from './onavToPairing';
import { matchKey } from './codes';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';

// The key values shown for a Mercadini wine profile
const PROFILE_KEYS = ['wine_body', 'wine_acidity', 'wine_tannicity'];

const ASPI_MARKS = ['gustatory_sugars', 'gustatory_alcohols', 'gustatory_polyols', 'gustatory_acids', 'gustatory_tannins', 'gustatory_minerals'];

// Who filled in the sheet, from the point of view of the current user
export function sheetOwnership(sheet, me) {
  if (!sheet.owner_id) return 'anonymous';
  return me && sheet.owner_id === me ? 'own' : 'other';
}

// Groups ONAV / ASPI sheets and Mercadini wine profiles (and the number of pairings) by wine. Wines without any sheet are not listed.
export function groupSheetsByWine(onavSheets, aspiSheets, pairings, me, profiles = []) {
  const wines = new Map();
  const entry = (sheet) => {
    const id = sheet.wine_id || (sheet.wine && sheet.wine.id);
    if (!id) return null;
    if (!wines.has(id)) {
      const wine = sheet.wine || {};
      wines.set(id, {
        id,
        name: wine.name || sheet.wine_name || '',
        vintage: wine.vintage || sheet.vintage || null,
        producer: wine.producer || sheet.producer || '',
        sheets: [],
        paired: 0,
      });
    }
    return wines.get(id);
  };
  [
    ...onavSheets.map((s) => ({ ...s, type: 'onav' })),
    ...aspiSheets.map((s) => ({ ...s, type: 'aspi' })),
    ...profiles.map((s) => ({ ...s, type: 'profile' })),
  ].forEach((sheet) => {
    const wine = entry(sheet);
    if (wine) wine.sheets.push(sheet);
  });
  pairings.forEach((pairing) => {
    const id = pairing.wine_id || (pairing.wine && pairing.wine.id);
    if (id && wines.has(id)) wines.get(id).paired += 1;
  });
  wines.forEach((wine) => {
    // own sheets first, then the most recent
    wine.sheets.sort((a, b) => {
      const ownA = sheetOwnership(a, me) === 'own' ? 0 : 1;
      const ownB = sheetOwnership(b, me) === 'own' ? 0 : 1;
      if (ownA !== ownB) return ownA - ownB;
      return String(b.tasting_date || b.created_at || '').localeCompare(String(a.tasting_date || a.created_at || ''));
    });
    wine.counts = { onav: 0, aspi: 0, profile: 0, own: 0, anonymous: 0, public: 0 };
    wine.sheets.forEach((sheet) => {
      wine.counts[sheet.type] += 1;
      const who = sheetOwnership(sheet, me);
      if (who === 'own') wine.counts.own += 1;
      else if (who === 'anonymous') wine.counts.anonymous += 1;
      else if (sheet.visibility === 'public') wine.counts.public += 1;
    });
  });
  return [...wines.values()].sort((a, b) => {
    if ((a.counts.own > 0) !== (b.counts.own > 0)) return a.counts.own > 0 ? -1 : 1;
    return matchKey(a.name).localeCompare(matchKey(b.name)) || (b.vintage || 0) - (a.vintage || 0);
  });
}

// A value of a Mercadini profile (0 is a real value) or '—' when not filled in
const shown = (value) => (isFilled(value) ? value : '—');

const sheetBadge = (type) => (type === 'aspi' ? 'ASPI' : type === 'profile' ? 'Mercadini' : 'ONAV');

const wineTitle = (wine) => `${wine.name}${wine.vintage ? ` ${wine.vintage}` : ''}`;

// Searchable list of the wines that have ONAV / ASPI sheets or Mercadini wine profiles, with badges telling which sheets
// exist, and the choice of ONE sheet (radio): no averaging between sheets, ever.
// Two actions on the chosen sheet: "Import" replaces all ten values with those of the sheet (what the sheet does not give stays
// not filled in) and "Complete empty values" fills only the parameters that are still empty (and is available only for a sheet of
// the same wine as the form: `currentWine` = { name, vintage, producer }).
// Props: onImport(result) where result =
// { mode: 'replace' | 'complete', wine: { name, vintage, producer }, sheet: { id, type }, values (only what the sheet gives),
//   direct, estimated };
// `profiles` (optional): the Mercadini wine profiles loaded by the parent (so that it can reload them after a save);
// without it the picker loads them itself.
function WineSheetPicker({ onImport, profiles: profilesProp, currentWine }) {
  const { t, formatDate } = useI18n();
  const [data, setData] = useState({ onav: [], aspi: [], pairings: [], profiles: [] });
  const [me, setMe] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error
  const [query, setQuery] = useState('');
  const [wineId, setWineId] = useState(null);
  const [sheetId, setSheetId] = useState(null);           // the ONE chosen sheet

  useEffect(() => {
    let abort = false;
    const load = async (path) => {
      const res = await authFetch(`${API_URL}${path}`);
      return res && res.ok ? res.json() : [];
    };
    (async () => {
      try {
        const [onav, aspi, pairings, ownProfiles, mine] = await Promise.all([
          load('/onav?limit=500'), load('/aspi?limit=500'), load('/pairings?limit=500'),
          profilesProp === undefined ? load('/wine-profiles?limit=200') : [], getCurrentUserId(),
        ]);
        if (abort) return;
        setData({ onav, aspi, pairings, profiles: ownProfiles });
        setMe(mine || null);
        setState('ok');
      } catch {
        if (!abort) setState('error');
      }
    })();
    return () => { abort = true; };
    // eslint-disable-next-line
  }, []);

  const profiles = Array.isArray(profilesProp) ? profilesProp : data.profiles;
  const wines = useMemo(() => groupSheetsByWine(data.onav, data.aspi, data.pairings, me, profiles), [data, me, profiles]);
  const filtered = useMemo(() => {
    const key = matchKey(query);
    if (!key) return wines;
    return wines.filter((wine) => matchKey(`${wine.name} ${wine.producer} ${wine.vintage || ''}`).includes(key));
  }, [wines, query]);
  const selected = wines.find((wine) => wine.id === wineId) || null;

  const choose = (wine) => {
    if (selected && selected.id === wine.id) { setWineId(null); return; }
    setWineId(wine.id);
    setSheetId(wine.sheets.length ? wine.sheets[0].id : null);       // the first one (yours first, then the most recent)
  };

  const chosenSheet = selected ? selected.sheets.find((sheet) => sheet.id === sheetId) || null : null;
  // Completing only makes sense on the wine of the form: otherwise the empty values would be filled with another wine's data
  const sameWine = (wine) => !!currentWine && !!matchKey(currentWine.name)
    && matchKey(wine.name) === matchKey(currentWine.name)
    && String(wine.vintage || '') === String(currentWine.vintage || '')
    && matchKey(wine.producer || '') === matchKey(currentWine.producer || '');

  const doImport = (mode) => {
    if (!selected || !chosenSheet) return;
    const converted = sheetToWineParams(chosenSheet);
    onImport({
      mode,
      wine: { name: selected.name, vintage: selected.vintage, producer: selected.producer },
      sheet: { id: chosenSheet.id, type: chosenSheet.type },
      values: converted.values, direct: converted.direct, estimated: converted.estimated,
    });
  };

  if (state === 'loading') return <div className="form-field import-onav"><small className="import-hint">{t('ui.loading')}</small></div>;
  if (state === 'error') return <div className="form-field import-onav"><small className="import-hint">{t('pairing.picker.error')}</small></div>;
  if (wines.length === 0) return <div className="form-field import-onav"><small className="import-hint">{t('pairing.picker.none')}</small></div>;

  const who = (sheet) => {
    const ownership = sheetOwnership(sheet, me);
    if (ownership === 'own') return t('pairing.picker.you');
    if (ownership === 'anonymous') return t('pairing.picker.anonymous');
    return sheet.taster || t('pairing.picker.other');
  };

  return (
    <div className="form-field import-onav wine-picker">
      <label>{t('pairing.import.label')}</label>
      <input type="search" className="wine-picker-search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder={t('pairing.picker.search')} aria-label={t('pairing.picker.search')} />
      <small className="import-hint">{t('pairing.import.hint')}</small>

      {filtered.length === 0 ? (
        <p className="empty">{t('pairing.picker.no_match')}</p>
      ) : (
        <ul className="wine-picker-list" role="listbox" aria-label={t('pairing.import.label')}>
          {filtered.map((wine) => (
            <li key={wine.id} role="option" aria-selected={selected && selected.id === wine.id}
              className={`wine-picker-wine${selected && selected.id === wine.id ? ' selected' : ''}`}>
              <button type="button" className="wine-picker-row" onClick={() => choose(wine)}>
                <span className="wine-picker-name">
                  <strong>{wineTitle(wine)}</strong>{wine.producer ? <span className="wine-picker-producer"> · {wine.producer}</span> : null}
                </span>
                <span className="wine-picker-badges">
                  {wine.counts.onav > 0 && <span className="wine-picker-badge onav">ONAV ×{wine.counts.onav}</span>}
                  {wine.counts.aspi > 0 && <span className="wine-picker-badge aspi">ASPI ×{wine.counts.aspi}</span>}
                  {wine.counts.profile > 0 && <span className="wine-picker-badge profile">Mercadini ×{wine.counts.profile}</span>}
                  {wine.paired > 0 && <span className="wine-picker-badge paired">{t('pairing.picker.paired', { count: wine.paired })}</span>}
                  {wine.counts.own > 0 && <span className="wine-picker-badge own">{t('pairing.picker.yours', { count: wine.counts.own })}</span>}
                  {wine.counts.public > 0 && <span className="wine-picker-badge public">{t('pairing.picker.public', { count: wine.counts.public })}</span>}
                  {wine.counts.anonymous > 0 && <span className="wine-picker-badge anon">{t('pairing.picker.anonymous_count', { count: wine.counts.anonymous })}</span>}
                </span>
              </button>

              {selected && selected.id === wine.id && (
                <div className="wine-picker-sheets">
                  <div className="wine-picker-sheets-title">{t('pairing.picker.sheets_title')}</div>
                  {wine.sheets.map((sheet) => (
                    <label key={`${sheet.type}-${sheet.id}`} className={`wine-picker-sheet${sheetId === sheet.id ? ' checked' : ''}`}>
                      <input type="radio" name="wine-picker-sheet" checked={sheetId === sheet.id} onChange={() => setSheetId(sheet.id)} />
                      <span className={`wine-picker-badge ${sheet.type}`}>{sheetBadge(sheet.type)}</span>
                      <span className="wine-picker-sheet-info">
                        {formatDate(sheet.tasting_date) || '—'} · {who(sheet)}
                        {' · '}<span className={`wine-picker-vis ${sheet.visibility}`}>{t(`pairing.picker.vis.${sheet.visibility}`)}</span>
                      </span>
                      <span className="wine-picker-sheet-key" title={sheet.type === 'aspi' ? t('pairing.picker.structure_legend') : undefined}>
                        {sheet.type === 'aspi'
                          ? `${t('pairing.picker.structure')}: ${ASPI_MARKS.map((field) => (sheet[field] > 0 ? sheet[field] : '–')).join(' · ')} /5`
                          : sheet.type === 'profile'
                            ? PROFILE_KEYS.map((field) => `${t(`pairing.p.${field}`)} ${shown(sheet[field])}`).join(' · ')
                            : (sheet.total > 0 ? `${sheet.total}/100` : '—')}
                      </span>
                    </label>
                  ))}
                  <div className="wine-picker-actions">
                    <button type="button" className="submit-btn" disabled={!chosenSheet} onClick={() => doImport('replace')}>
                      {t('pairing.picker.import')}
                    </button>
                    <button type="button" className="reset-btn" disabled={!chosenSheet || !sameWine(wine)} onClick={() => doImport('complete')}
                      title={sameWine(wine) ? t('pairing.picker.complete_hint') : t('pairing.picker.other_wine')}>
                      {t('pairing.picker.complete')}
                    </button>
                    <small className="import-hint">{sameWine(wine) ? t('pairing.picker.import_hint') : `${t('pairing.picker.import_hint')} ${t('pairing.picker.other_wine')}`}</small>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default WineSheetPicker;
